// Parser CSV des exports Booking.com + logique d'import (plan + apply).
//
// parseBookingCsv(text) → { rows, warnings }
//     Transforme le CSV brut en lignes typées. Gère les 2 formats :
//     - 14 colonnes (sans Tourism tax)
//     - 15 colonnes (avec Tourism tax en position 9)
//     Détection automatique via présence de "Tourism tax" dans le header.
//
// buildBookingImportPlan(data, rows, logId, opts) → { toUpdate, toCreate, ignored, summary }
//     Signature différente de Airbnb : logId REQUIS (pas de mapping auto).
//     opts.menageForfait : nombre optionnel, utilisé pour remplir resa.menage par défaut.
//     opts.datePlancher : ISO date (défaut '2026-01-01').
//     Dédup par bookingRef + fallback checkIn ISO.
//     Pas d'agrégation (1 résa = 1 ligne, contrairement à Airbnb fractionné).
//
// applyBookingImportPlan(data, plan) → newData
//     Force plateforme:'booking', stocke bookingRef (pas airbnbCode), préfixe id 'bdc_'.
//     Parallèle indépendant de applyImportPlan Airbnb.
//
// Format source Booking :
//   - UTF-8 (parfois avec BOM)
//   - Séparateur : virgule
//   - Champs contenant virgule : quotés avec guillemets
//   - Dates : "19 Jan 2026" (EN, mois 3 lettres) OU "3 mai 2026" (FR, mois en
//     toutes lettres) — espaces parfois insécables (U+00A0 / U+202F).
//   - Décimales : POINT (ex : 123.45) — jamais de virgule décimale ni de
//     séparateur de milliers sur ce format (octets réels prouvés 2026-06).
//   - En-têtes : EN ("Reference number"…) OU FR ("Numéro de réservation"…),
//     potentiellement mojibakés (UTF-8 lu comme Latin-1, ex. "rÃ©servation").
//
// Le dé-mojibake (repairMojibake) doit s'appliquer sur le TEXTE BRUT en
// ENTRÉE du parse, AVANT toute détection / matching d'en-tête.

// ─────────────────────────────────────────────────────────────────────────────
// Helpers (dupliqués depuis csv-airbnb.js — scope volontairement localisé
// jusqu'à ce qu'un 3e parser émerge pour justifier un csv-common.js)
// ─────────────────────────────────────────────────────────────────────────────

function splitCsvLine(line) {
  const out = []
  let cur = ''
  let inQuote = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (inQuote) {
      if (ch === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++ }
        else { inQuote = false }
      } else {
        cur += ch
      }
    } else {
      if (ch === '"') inQuote = true
      else if (ch === ',') { out.push(cur); cur = '' }
      else cur += ch
    }
  }
  out.push(cur)
  return out
}

// parseNumber — montants Booking. Format prouvé (octets réels 2026-06) :
//   - décimale = POINT (jamais virgule, jamais séparateur de milliers)
//   - signe préservé (Net/Commission/Fee peuvent être négatifs ; la garde
//     `netVal <= 0` du plan en dépend)
// On se limite donc à retirer les espaces (normaux + insécables U+00A0/U+202F)
// que le mojibake / l'export peut glisser autour du nombre, puis parseFloat.
// Pas d'heuristique virgule↔point : les octets prouvent le point décimal, en
// inventer une serait un risque chemin-argent (mauvaise interprétation d'un
// "1,234" qui n'existe pas dans ce format).
function parseNumber(s) {
  if (s == null || s === '') return null
  const cleaned = String(s).replace(/[\s  ]/g, '')
  if (cleaned === '') return null
  const n = parseFloat(cleaned)
  return isNaN(n) ? null : n
}

// ─────────────────────────────────────────────────────────────────────────────
// Dé-mojibake + normalisation (FR Booking)
// ─────────────────────────────────────────────────────────────────────────────

// Marqueurs typiques d'un texte UTF-8 décodé à tort en Latin-1.
// (Ã©=é, Ã¨=è, Ã =à, Ã¡=á, â€=guillemets/apostrophe typographiques, Â=nbsp…)
const MOJIBAKE_MARKERS = /Ã©|Ã¨|Ã |Ã¡|Ã´|Ã®|Ã¯|Ã§|Ãª|Ã«|Ã¹|Ã»|Ã¼|Ã¢|â€|Â /

// repairMojibake(text) — répare un texte UTF-8-lu-comme-Latin-1.
// SI marqueurs détectés sur la 1re ligne → ré-interprète chaque code-unit
// comme un octet et re-décode en UTF-8 strict. En cas d'octet invalide
// (throw) ou de caractère de remplacement U+FFFD dans le résultat → on
// RETOURNE LE TEXTE ORIGINAL (ne jamais émettre du corrompu). No-op si aucun
// marqueur (préserve le texte propre, y compris CJK « 北京 »).
function repairMojibake(text) {
  if (!text || typeof text !== 'string') return text
  const firstLine = text.split(/\r?\n/, 1)[0] || ''
  if (!MOJIBAKE_MARKERS.test(firstLine)) return text
  try {
    const bytes = Uint8Array.from(text, (c) => c.charCodeAt(0))
    const decoded = new TextDecoder('utf-8', { fatal: true }).decode(bytes)
    if (decoded.includes('�')) return text
    return decoded
  } catch (e) {
    // Octet hors plage / séquence UTF-8 invalide → on garde l'original.
    return text
  }
}

// normalizeHeader(s) — clé de comparaison tolérante pour libellés de colonnes :
// trim + lowercase + suppression des diacritiques + collapse des espaces
// (normaux ET insécables U+00A0/U+202F) en un seul espace simple.
function normalizeHeader(s) {
  if (s == null) return ''
  return String(s)
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[\s  ]+/g, ' ')
    .trim()
}

// ─────────────────────────────────────────────────────────────────────────────
// Spécifique Booking
// ─────────────────────────────────────────────────────────────────────────────

const MONTHS_EN = {
  jan: '01', feb: '02', mar: '03', apr: '04', may: '05', jun: '06',
  jul: '07', aug: '08', sep: '09', oct: '10', nov: '11', dec: '12',
}

// Mois FR en toutes lettres (clés sans diacritique → cf. normalizeHeader).
const MONTHS_FR = {
  janvier: '01', fevrier: '02', mars: '03', avril: '04', mai: '05', juin: '06',
  juillet: '07', aout: '08', septembre: '09', octobre: '10',
  novembre: '11', decembre: '12',
  // Abrégés (vrai relevé de janvier à mai 2026 : « 5 avr. 2026 »). Le point
  // final est retiré avant la recherche ; mars, mai, juin, aout, oct, nov
  // sont déjà couverts.
  janv: '01', fev: '02', fevr: '02', avr: '04', juil: '07', sept: '09', dec: '12',
}

// "19 Jan 2026" (EN) ou "3 mai 2026" (FR toutes lettres) → "2026-01-19".
// Tolère les espaces insécables (U+00A0/U+202F) entre les composants.
// Retourne null si format invalide ou vide.
function parseBookingDate(s) {
  if (!s) return null
  // Normalise les espaces insécables AVANT match (le mojibake réparé contient
  // de vrais U+00A0). Pas de strip diacritique ici : on le fait sur le mois.
  const norm = String(s).replace(/[\s  ]+/g, ' ').trim()
  // jour + mois (lettres, accents possibles) + année
  const m = norm.match(/^(\d{1,2})\s+([A-Za-zÀ-ÿ]+)\.?\s+(\d{4})$/)
  if (!m) return null
  const [, dd, mon, yyyy] = m
  const monKey = mon.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase()
  const mm = MONTHS_EN[monKey] || MONTHS_FR[monKey]
  if (!mm) return null
  return `${yyyy}-${mm}-${dd.padStart(2, '0')}`
}

// normalizeTypeValue(s) — canonicalise la VALEUR de la colonne Type.
// « Réservation » / « Reservation » / mojibake réparé → 'Reservation'
// (valeur canonique attendue par le filtre du plan). Toute autre valeur
// (credit_note, Commission adjustment…) est laissée telle quelle (trim).
function normalizeTypeValue(s) {
  const trimmed = (s == null ? '' : String(s)).trim()
  if (normalizeHeader(trimmed) === 'reservation') return 'Reservation'
  return trimmed
}

// Calcul nuits entre 2 dates ISO (checkOut - checkIn).
function diffDaysISO(checkIn, checkOut) {
  if (!checkIn || !checkOut) return null
  const d1 = new Date(checkIn + 'T00:00:00Z')
  const d2 = new Date(checkOut + 'T00:00:00Z')
  const diff = (d2 - d1) / (1000 * 60 * 60 * 24)
  return diff > 0 ? Math.round(diff) : null
}

// Colonnes attendues. On cherche par nom (pas par position), de façon TOLÉRANTE :
// chaque colonne canonique (clé EN interne, inchangée pour ne pas perturber la
// logique cas A/B + le matching argent) a une liste d'alias normalisés (EN + FR).
// La comparaison passe par normalizeHeader (accents/casse/insécables neutralisés).
// La présence de 'Tourism tax' signale toujours le format 15-colonnes.
const BOOKING_KNOWN_COLUMNS = [
  'Type', 'Reference number', 'Check-in', 'Checkout', 'Guest name',
  'Reservation status', 'Currency', 'Payment status', 'Tourism tax',
  'Amount', 'Commission', 'Payments Service Fee', 'Net',
  'Payout date', 'Payout ID',
]

// canonique → alias normalisés (via normalizeHeader) acceptés en en-tête.
const BOOKING_COLUMN_ALIASES = {
  'Type': ['type'],
  'Reference number': ['reference number', 'numero de reservation', 'numero de reference'],
  'Check-in': ['check-in', 'checkin', 'arrivee'],
  'Checkout': ['checkout', 'check-out', 'depart'],
  'Guest name': ['guest name', 'nom du client'],
  'Reservation status': ['reservation status', 'statut de la reservation'],
  'Currency': ['currency', 'devise'],
  'Payment status': ['payment status', 'statut du paiement'],
  'Tourism tax': ['tourism tax', 'taxe de sejour', 'taxe touristique'],
  'Amount': ['amount', 'montant'],
  'Commission': ['commission'],
  'Payments Service Fee': ['payments service fee', 'frais de service de paiement'],
  'Net': ['net'],
  'Payout date': ['payout date', 'date du paiement'],
  'Payout ID': ['payout id', 'identifiant du paiement'],
}

// Colonnes FACULTATIVES qui nomment l'annonce / la chambre (Maison Zen = une
// propriété Booking, une annonce par chambre). Les relevés de paiement réels
// vus jusqu'ici (14/15 colonnes ci-dessus) N'EN ONT PAS : l'écran propose
// alors une chambre et Dimitri valide. Ordre = priorité (chambre avant
// établissement). Alias normalisés (normalizeHeader).
const BOOKING_ANNONCE_ALIASES = [
  'room name', 'room type', 'unit name', 'unit type', 'listing name',
  'accommodation name', 'accommodation type',
  'nom de la chambre', 'type de chambre', 'nom de l\'annonce', 'annonce',
  'nom de l\'hebergement', 'hebergement', 'type d\'hebergement', 'logement',
  'property name', 'nom de l\'etablissement', 'etablissement',
]

function buildBookingAnnonceIndexes(header) {
  const normHeader = header.map(normalizeHeader)
  const out = []
  for (const a of BOOKING_ANNONCE_ALIASES) {
    const pos = normHeader.indexOf(a)
    if (pos !== -1 && !out.includes(pos)) out.push(pos)
  }
  return out
}

// Construit l'index canonique→position à partir des en-têtes (normalisés).
function buildBookingHeaderIndex(header) {
  const normHeader = header.map(normalizeHeader)
  const idx = {}
  for (const col of BOOKING_KNOWN_COLUMNS) {
    const aliases = BOOKING_COLUMN_ALIASES[col] || [normalizeHeader(col)]
    let pos = -1
    for (const a of aliases) {
      pos = normHeader.indexOf(a)
      if (pos !== -1) break
    }
    idx[col] = pos
  }
  return idx
}

function parseBookingCsv(text) {
  const warnings = []
  const rows = []
  if (!text) return { rows, warnings }

  // 1) Dé-mojibake AVANT tout (en-têtes + valeurs deviennent du texte propre).
  const repaired = repairMojibake(text)
  // 2) Retire un éventuel BOM UTF-8 (﻿) en tête.
  const clean = repaired.replace(/^﻿/, '')
  const lines = clean.split(/\r?\n/).filter(l => l.trim().length > 0)
  if (lines.length === 0) return { rows, warnings }

  const header = splitCsvLine(lines[0]).map(c => c.trim())
  const idx = buildBookingHeaderIndex(header)
  const annonceIdx = buildBookingAnnonceIndexes(header)

  // Sanity : on exige au minimum Type, Reference number, Check-in, Net.
  if (idx['Type'] === -1 || idx['Reference number'] === -1 || idx['Check-in'] === -1 || idx['Net'] === -1) {
    warnings.push({
      line: 1,
      reason: 'header_invalid (colonnes manquantes parmi Type/Reference number/Check-in/Net — format Booking modifié ?)',
    })
    return { rows, warnings }
  }

  // Compteurs des lignes hétérogènes détectées ligne-par-ligne (cas A et cas B).
  // L'approche globale précédente (fix cas A sur lines[1]) était fragile :
  // si la 1re ligne de data est un credit_note à 14 valeurs alors que les
  // Reservation sont à 15 valeurs, le fix global ne se déclenchait pas.
  // Désormais les 2 cas sont gérés uniformément ligne-par-ligne.
  //   Cas A : header 14 cols sans Tourism tax + ligne 15 valeurs → Tourism tax présente, post-Payment status décalé +1
  //   Cas B : header 15 cols avec Tourism tax + ligne 14 valeurs → Tourism tax absente, post-Payment status décalé -1
  let rowsAvecTourismTaxCount = 0
  let rowsSansTourismTaxCount = 0

  for (let i = 1; i < lines.length; i++) {
    const cols = splitCsvLine(lines[i])
    if (cols.length < 10) {
      warnings.push({ line: i + 1, reason: `too_few_columns (${cols.length} < 10)` })
      continue
    }
    // Détection par ligne — gère les 2 cas d'hétérogénéité Tourism tax.
    let lineIdx = idx
    if (cols.length === 15 && header.length === 14 && idx['Tourism tax'] === -1) {
      // Cas A : ligne à 15 valeurs dans un header 14 cols (Tourism tax présente sur la ligne).
      lineIdx = { ...idx,
        'Tourism tax': 8,
        'Amount': 9,
        'Commission': 10,
        'Payments Service Fee': 11,
        'Net': 12,
        'Payout date': 13,
        'Payout ID': 14,
      }
      rowsAvecTourismTaxCount++
    } else if (cols.length === 14 && header.length === 15 && idx['Tourism tax'] === 8) {
      // Cas B : ligne à 14 valeurs dans un header 15 cols (Tourism tax absente sur la ligne).
      lineIdx = { ...idx,
        'Tourism tax': -1,
        'Amount': 8,
        'Commission': 9,
        'Payments Service Fee': 10,
        'Net': 11,
        'Payout date': 12,
        'Payout ID': 13,
      }
      rowsSansTourismTaxCount++
    }
    const get = (h) => {
      const j = lineIdx[h]
      return j >= 0 && j < cols.length ? cols[j] : ''
    }

    rows.push({
      raw: i + 1,
      // Canonicalise la valeur Type (« Réservation »/mojibake → 'Reservation')
      // pour que le filtre du plan (r.type !== 'Reservation') laisse passer
      // les résas FR. SANS ça l'import FR resterait vide.
      type: normalizeTypeValue(get('Type')),
      bookingRef: (get('Reference number') || '').trim() || null,
      checkIn: parseBookingDate(get('Check-in')),
      checkOut: parseBookingDate(get('Checkout')),
      guestName: (get('Guest name') || '').trim(),
      status: (get('Reservation status') || '').trim().toLowerCase(),
      currency: (get('Currency') || '').trim().toUpperCase(),
      paymentStatus: (get('Payment status') || '').trim(),
      tourismTax: parseNumber(get('Tourism tax')),
      amount: parseNumber(get('Amount')),
      commission: parseNumber(get('Commission')),
      fee: parseNumber(get('Payments Service Fee')),
      net: parseNumber(get('Net')),
      payoutDate: parseBookingDate(get('Payout date')),
      // Identifiant du versement (point A : séjours payés en plusieurs fois).
      payoutId: (get('Payout ID') || '').trim() || null,
      // Nom d'annonce / de chambre lu dans le relevé (null si absent).
      annonce: annonceIdx
        .map((j) => (j < cols.length ? String(cols[j] || '').trim() : ''))
        .filter(Boolean)
        .join(' | ') || null,
    })
  }

  if (rowsAvecTourismTaxCount > 0) {
    warnings.push({
      line: 0,
      reason: `header_incomplet_tourism_tax_decalee (${rowsAvecTourismTaxCount} ligne(s) à 15 valeurs dans un header 14 cols — Tourism tax présente par ligne malgré header incomplet, colonnes post-Payment status décalées de +1)`,
    })
  }
  if (rowsSansTourismTaxCount > 0) {
    warnings.push({
      line: 0,
      reason: `lignes_sans_tourism_tax_decalees (${rowsSansTourismTaxCount} ligne(s) à 14 valeurs dans un header 15 cols — Tourism tax absente par ligne, colonnes post-Payment status décalées de -1)`,
    })
  }

  return { rows, warnings }
}

// ─────────────────────────────────────────────────────────────────────────────
// Construction et application du plan d'import Booking
// ─────────────────────────────────────────────────────────────────────────────

// Plan B (ADR-182) — bucket par majorité de nuits + garde plancher création
// + clé identité stable pour la dédup cross-bucket. Source = copie src/lib
// (ESM transpilée Babel, require interop OK ; require('../../lib/...') interdit
// par CRA car hors de src/).
const {
  computeInvoiceMonthByNights,
  canCreateMonth,
  resaIdentityKey,
} = require('./resa-invoice-month')
// Maison Zen (décision Dimitri 03/10) : marqueur d'origine CSV + date d'import.
const { ORIGINE_CSV_BOOKING , moisEffectifDuCode } = require('./csv-bascule')
const { resolveListingId, normalizeListingName } = require('./airbnb-map')
// Parts par versement (séjours payés en plusieurs fois, point A relecture).
const { ajouterPart, fusionnerParts, sommeParts, sommeNegatives, partsValides, PART_CORRECTION, dejaFactureAilleurs, dedoublonnerPartsDatees } = require('./csv-versements')
const _partBooking = (r) => ({
  // Date du versement (affichée) ; l'identifiant reste Payout ID / date.
  date: r.payoutDate || null,
  net: r.net, amount: r.amount, commission: r.commission, fee: r.fee, tourismTax: r.tourismTax,
})
// Identifiant de versement Booking : « Payout ID », à défaut « Payout date ».
const _idVersementBooking = (r) => (r && (r.payoutId || r.payoutDate)) || null

const DATE_PLANCHER_DEFAULT = '2026-01-01'
const monthFromDate = (isoDate) => (isoDate ? isoDate.slice(0, 7) : null)

// Plan B — index cross-bucket des résas d'un logement par clé identité stable.
// Parcourt TOUS les buckets 'YYYY-MM' de data → Map(key → mk) (premier hit).
function _buildCrossBucketIndex(data, logId) {
  const idx = new Map()
  if (!data) return idx
  for (const mk of Object.keys(data)) {
    if (!/^\d{4}-\d{2}$/.test(mk)) continue
    const resas = data[mk]?.[logId]?.reservations
    if (!Array.isArray(resas)) continue
    for (const r of resas) {
      const key = resaIdentityKey(r)
      if (key && !idx.has(key)) idx.set(key, mk)
    }
  }
  return idx
}

// Helper local — lit les bookingRefs tombstones pour un logement donné.
// Fallback rétrocompat : si `data._tombstones[lid].booking_refs` absent
// → Set vide (comportement inchangé). Cf. plan fix iCal 2026-05-23 (B-3).
function _getDeletedBookingRefs(data, lid) {
  const arr = data?._tombstones?.[lid]?.booking_refs
  if (!Array.isArray(arr)) return new Set()
  return new Set(arr.map(t => t.bookingRef).filter(Boolean))
}

const _r2 = (n) => Math.round((Number(n) || 0) * 100) / 100

// Statistiques (champs ORIGINE, comme transformV2Resa et les manuelles) :
//   ota_commission   |Commission| + |Payments Service Fee| (Beds24 agrège
//                    pareil commission + frais de paiement)
//   ca_brut_origine  Net + commission = Amount − taxe de séjour (Amount la
//                    comprend, Booking la reverse : elle n'est pas du CA)
//   montant_origine  Net importé (= montant, inchangé)
// Identité tenue : ca_brut_origine − montant − taxe(0) = ota_commission.
// Vérifié sur de vrais relevés : 1140,80 − 68,24 = 875,20 + 182,34 + 15,02.
// Sans Commission ni frais : brut = Amount − taxe si Amount est là ; sinon
// aucun champ (repli « brut = net » des statistiques), jamais un 0 inventé.
function _origineStatsBooking(r, netVal) {
  const fini = (x) => x != null && Number.isFinite(Number(x))
  if (fini(r.commission) || fini(r.fee)) {
    const ota = _r2(Math.abs(Number(r.commission) || 0) + Math.abs(Number(r.fee) || 0))
    return { ca_brut_origine: _r2(netVal + ota), ota_commission: ota, montant_origine: _r2(netVal) }
  }
  if (fini(r.amount)) {
    const brut = _r2(Number(r.amount) - Math.abs(Number(r.tourismTax) || 0))
    return { ca_brut_origine: brut, ota_commission: _r2(brut - netVal), montant_origine: _r2(netVal) }
  }
  return {}
}

// `logIdOuChoix` : l'id du logement (toutes les lignes y vont, comportement
// historique) OU une fonction (ligne) → id de chambre | null — Maison Zen, une
// chambre par ligne, choisie à l'écran. null → ligne ignorée, jamais devinée.
// opts.v2Lids : chambres facturées par la V2 — le rapprochement par date
// d'arrivée n'y écrase plus une ligne (seule la référence Booking rapproche).
// opts.chambresGroupe : chambres de la même propriété Booking — une référence
// déjà rangée dans UNE AUTRE chambre n'est pas recréée ici (ignorée, motif dit).
function buildBookingImportPlan(data, rows, logIdOuChoix, opts = {}) {
  const parChoix = typeof logIdOuChoix === 'function'
  if (!parChoix && !logIdOuChoix) throw new Error('buildBookingImportPlan : logId requis')
  const choisirLogId = parChoix ? logIdOuChoix : () => logIdOuChoix
  const datePlancher = opts.datePlancher || DATE_PLANCHER_DEFAULT
  const menageForfait = Number(opts.menageForfait) || 0
  const v2Lids = new Set(Array.isArray(opts.v2Lids) ? opts.v2Lids : [])
  const chambresGroupe = Array.isArray(opts.chambresGroupe) ? opts.chambresGroupe : []
  // D1 : (logId, mois) → true si le mois est déjà facturé (import refusé).
  const moisGele = typeof opts.moisGele === 'function' ? opts.moisGele : null
  // D (relecture R1) : (code) → { lid, mk } si la V2 de ce code est dans un
  // mois facturé — import refusé même si le mois du relevé est libre.
  const codeGeleV2 = typeof opts.codeGeleV2 === 'function' ? opts.codeGeleV2 : null

  const toUpdate = []
  const toCreate = []
  const ignored = []
  const reasons = {}
  const bumpReason = (k) => { reasons[k] = (reasons[k] || 0) + 1 }

  // Plan B — index cross-bucket, un par logement (un seul en mode historique).
  const crossIdxCache = new Map()
  const getCrossIdx = (lid) => {
    if (!crossIdxCache.has(lid)) crossIdxCache.set(lid, _buildCrossBucketIndex(data, lid))
    return crossIdxCache.get(lid)
  }

  // Regroupement par référence (doublon d'argent relevé par l'audit) : une
  // même référence peut revenir sur plusieurs lignes « Reservation » (relevé
  // couvrant deux virements, fichiers mis bout à bout). Sans regroupement :
  // deux créations (net compté deux fois), ou la dernière ligne écrasait la
  // première. Comme Airbnb, on regroupe AVANT le plan. Règle par colonne :
  //   ADDITIONNÉES : net, amount, commission, fee, tourismTax (lignes de
  //     paiement d'un même séjour ; un remboursement négatif s'en déduit) ;
  //   PREMIÈRE LIGNE : dates, voyageur, statut, annonce (identité du séjour).
  // Les filtres type / devise / référence passent ligne par ligne, avant ;
  // date plancher et net ≤ 0 portent sur la réservation regroupée.
  // Remboursements sur une réservation déjà comptée (point B relecture).
  const remboursements = []
  // Tour 3 point 2 : corrections à la main gardées comme une part (annoncées).
  const correctionsManuelles = []
  const regroupees = []
  const parRef = new Map()
  const regroupements = []
  const somme = (a, b) => (a == null && b == null ? null : (Number(a) || 0) + (Number(b) || 0))
  for (const r of rows) {
    const eligible = r && r.type === 'Reservation' && r.currency === 'EUR' && r.bookingRef
    if (!eligible) { regroupees.push(r); continue }
    const prev = parRef.get(r.bookingRef)
    if (!prev) {
      const idv = _idVersementBooking(r)
      const g = { ...r, rawLines: [r.raw], _nets: [Number(r.net) || 0],
        // Parts par versement : toutes les lignes doivent porter un
        // identifiant, sinon comportement d'avant (aucune part).
        parts: idv ? ajouterPart({}, idv, _partBooking(r)) : null }
      parRef.set(r.bookingRef, g)
      regroupees.push(g)
      continue
    }
    for (const col of ['net', 'amount', 'commission', 'fee', 'tourismTax']) {
      prev[col] = somme(prev[col], r[col])
      if (prev[col] != null) prev[col] = _r2(prev[col])
    }
    const idv = _idVersementBooking(r)
    prev.parts = prev.parts && idv ? ajouterPart(prev.parts, idv, _partBooking(r)) : null
    prev.rawLines.push(r.raw)
    prev._nets.push(Number(r.net) || 0)
  }
  for (const g of parRef.values()) {
    if (g.rawLines.length > 1) {
      regroupements.push({ bookingRef: g.bookingRef, lignes: g.rawLines.slice(), nets: g._nets.slice(), total: _r2(g.net) })
    }
  }

  for (const r of regroupees) {
    const lignesSource = r.rawLines || [r.raw]
    // Filtre type : Reservation uniquement (ignore credit_note, Commission adjustment)
    if (r.type !== 'Reservation') {
      bumpReason('type_' + (r.type || 'vide').toLowerCase().replace(/\s+/g, '_'))
      ignored.push({ lines: lignesSource, bookingRef: r.bookingRef, guestName: r.guestName, checkIn: r.checkIn, reason: 'type:' + (r.type || 'vide') })
      continue
    }
    if (r.currency !== 'EUR') {
      bumpReason('devise_non_eur')
      ignored.push({ lines: lignesSource, bookingRef: r.bookingRef, guestName: r.guestName, checkIn: r.checkIn, reason: 'devise:' + r.currency })
      continue
    }
    if (!r.bookingRef) {
      bumpReason('booking_ref_manquant')
      ignored.push({ lines: lignesSource, bookingRef: null, guestName: r.guestName, checkIn: r.checkIn, reason: 'booking_ref_manquant' })
      continue
    }
    if (!r.checkIn || r.checkIn < datePlancher) {
      bumpReason('avant_date_plancher')
      ignored.push({ lines: lignesSource, bookingRef: r.bookingRef, guestName: r.guestName, checkIn: r.checkIn, reason: 'avant_date_plancher:' + datePlancher })
      continue
    }
    let netVal = Number(r.net) || 0
    // AVEC parts de versement : décidé après rapprochement (un remboursement
    // sur une réservation comptée est une part négative qui s'y soustrait).
    if (!r.parts && netVal <= 0) {
      // Annulées non payées, refunds, etc.
      bumpReason('net_non_positif')
      ignored.push({ lines: lignesSource, bookingRef: r.bookingRef, guestName: r.guestName, checkIn: r.checkIn, reason: 'net_non_positif' })
      // Point B : remboursement sur une réservation déjà comptée → signalé
      // (le montant compté ne bouge pas sans identifiant de versement).
      if (netVal < 0) {
        const lidChoisi = choisirLogId(r)
        const candidats = [...new Set([lidChoisi, ...chambresGroupe].filter(Boolean))]
        for (const lid of candidats) {
          if (_getDeletedBookingRefs(data, lid).has(r.bookingRef)) continue
          const mk = getCrossIdx(lid).get('bk:' + r.bookingRef)
          const x = mk ? (data[mk][lid].reservations || []).find(y => y && y.bookingRef === r.bookingRef) : null
          if (x && Number(x.montant) > 0) {
            remboursements.push({ code: r.bookingRef, lid, rembourse: _r2(netVal), compte: _r2(x.montant) })
            break
          }
        }
      }
      continue
    }

    const logId = choisirLogId(r)
    if (!logId) {
      bumpReason('chambre_non_choisie')
      ignored.push({ lines: lignesSource, bookingRef: r.bookingRef, guestName: r.guestName, checkIn: r.checkIn, reason: 'chambre_non_choisie' })
      continue
    }
    // Une référence SUPPRIMÉE d'une autre chambre (marque posée par
    // deleteResaV2) n'y est plus rangée : elle ne bloque pas le réimport ici.
    const autreChambre = chambresGroupe.find(
      (lid) => lid !== logId && getCrossIdx(lid).has('bk:' + r.bookingRef) &&
        !_getDeletedBookingRefs(data, lid).has(r.bookingRef)
    )
    if (autreChambre) {
      bumpReason('deja_dans_une_autre_chambre')
      ignored.push({ lines: lignesSource, bookingRef: r.bookingRef, guestName: r.guestName, checkIn: r.checkIn, reason: 'deja_dans_une_autre_chambre:' + autreChambre })
      continue
    }
    const crossIdx = getCrossIdx(logId)

    // Plan B — bucket cible = mois de MAJORITÉ DE NUITS (ADR-182). Fallback
    // monthFromDate si helper null. Dédup cross-bucket par clé bk:<ref> :
    // si la résa vit déjà ailleurs → update in-place là-bas (préserve un
    // éventuel month_override). Sinon → bucket naturel.
    const naturalMonth = computeInvoiceMonthByNights(r.checkIn, r.checkOut)
      || monthFromDate(r.checkIn)
    const identKey = r.bookingRef ? 'bk:' + r.bookingRef : null
    const existingBucket = identKey ? crossIdx.get(identKey) : null
    const month = existingBucket || naturalMonth

    // D1 (décision Dimitri 03/10) : mois déjà facturé (facture voyageur V
    // émise sur ce logement × mois) → ni création ni mise à jour, motif dit.
    // Relecture tour 3 (point 4) : le gel teste le mois EFFECTIF (déplacement
    // de mois _monthOverrides compris), pas seulement le mois de rangement.
    const moisEffectif = moisEffectifDuCode(data, logId, r.bookingRef, month)
    const gelMois = moisGele ? moisGele(logId, moisEffectif) : false
    if (gelMois) {
      // Motif nommé : facture de l'app, ou mois facturé hors app (tour 4).
      const motifGel = (gelMois && gelMois.motif) || 'mois_facture'
      bumpReason(motifGel)
      ignored.push({ lines: lignesSource, bookingRef: r.bookingRef, guestName: r.guestName, checkIn: r.checkIn, logId, reason: motifGel + ':' + moisEffectif })
      continue
    }

    const geleV2 = codeGeleV2 ? codeGeleV2(r.bookingRef) : null
    if (geleV2) {
      bumpReason('mois_facture')
      ignored.push({ lines: lignesSource, bookingRef: r.bookingRef, guestName: r.guestName, checkIn: r.checkIn, logId: geleV2.lid, reason: 'mois_facture:' + geleV2.mk })
      continue
    }

    const existingResas = data?.[month]?.[logId]?.reservations || []
    const deletedBookingSet = _getDeletedBookingRefs(data, logId)

    // Priorité : match par bookingRef (stable). Fallback : match par checkIn ISO.
    let match = existingResas.find(x => x.bookingRef && x.bookingRef === r.bookingRef)
    if (!match && !v2Lids.has(logId)) match = existingResas.find(x => (x.checkIn || x.date) === r.checkIn && x.plateforme === 'booking')

    const nuits = diffDaysISO(r.checkIn, r.checkOut) || 1
    // Taxe de séjour : collectée et reversée par Booking, elle ne transite pas
    // par l'hôte — même règle que la V2 (correctif #681, transformV2Resa :
    // Booking ≡ Airbnb, taxe 0). La colonne reste lue (r.tourismTax) mais
    // n'entre ni dans `taxe` ni dans le net.
    const TAXE_BOOKING = 0
    // Point A : parts par versement — fusion avec celles déjà enregistrées
    // (un versement vu remplace sa part), net = somme des parts.
    let eff = r
    let partsFinales = null
    // Relecture tour 3 (point 3) : une ligne SANS identifiant de versement ne
    // remplace jamais une réservation déjà détaillée par versements — refusée,
    // motif visible, montant inchangé.
    if (!r.parts && match && partsValides(match.parts_versements)) {
      bumpReason('versement_sans_identifiant')
      ignored.push({ lines: lignesSource, bookingRef: r.bookingRef, guestName: r.guestName, checkIn: r.checkIn, logId, reason: 'versement_sans_identifiant' })
      continue
    }
    // Tour 5 (décision du 06/10, « 1 ok ») — garde « déjà facturé
    // ailleurs » TOUJOURS, avec ou sans détail de versements : avant toute
    // création, et avant toute mise à jour d'une réservation sans détail (ou
    // à 0 €). Une ligne d'un mois GELÉ du même logement, même période
    // (arrivée + nuits) et même montant → refus nommé ; prénoms différents
    // (les deux connus) → « à trancher », jamais libre.
    const sansDetail = !(match && partsValides(match.parts_versements))
    const cibleGarde = sansDetail ? {
      exclureId: match ? match.id : null, voyageur: r.guestName,
      checkIn: r.checkIn, checkOut: r.checkOut, nuits: r.nuits,
      montant: r.parts ? (sommeParts(r.parts, 'net') || 0) : _r2(r.net),
    } : null
    if (cibleGarde && moisGele) {
      const dejaFacture = dejaFactureAilleurs(data, logId, cibleGarde, moisGele)
      if (dejaFacture) {
        // deja_facture | a_trancher_deja_facture | a_trancher_chevauchement
        const motif = dejaFacture.motif || 'deja_facture'
        bumpReason(motif)
        ignored.push({ lines: lignesSource, bookingRef: r.bookingRef, guestName: r.guestName, checkIn: r.checkIn, logId, reason: motif + ':' + dejaFacture.mk + ':' + (dejaFacture.numero || '?') })
        continue
      }
    }
    // Décision Dimitri 06/10 (« ok refus », remplace la règle 3b ; tour 5 :
    // sans identifiant de versement aussi) : réservation existante SANS
    // détail de versements, montant ≠ 0, qui reçoit un versement → refus,
    // impossible de savoir s'il est déjà compté ; rien n'est ajouté ni
    // remplacé. Une ligne à 0 € reçoit normalement le versement.
    const ancienMontant = match && sansDetail ? Number(match.montant) : 0
    if (match && sansDetail && Number.isFinite(ancienMontant) && Math.abs(ancienMontant) > 0.005) {
      bumpReason('avant_suivi_versements')
      ignored.push({ lines: lignesSource, bookingRef: r.bookingRef, guestName: r.guestName, checkIn: r.checkIn, logId, reason: 'avant_suivi_versements', versements: r.parts ? Object.keys(r.parts).map((k) => ({ date: r.parts[k].date || null, montant: r.parts[k].net })) : [{ date: r.payoutDate || null, montant: _r2(r.net) }] })
      continue
    }
    if (r.parts) {
      let dejaParts = match && partsValides(match.parts_versements) ? match.parts_versements : null
      // Relecture tour 3 (point 2) : montant corrigé à la main (écart > 0,01
      // avec la somme du détail) → l'écart est GARDÉ comme une part
      // « correction manuelle » et annoncé ; jamais perdu en silence.
      if (dejaParts) {
        const ecart = _r2(Number(match.montant) - (sommeParts(dejaParts, 'net') || 0))
        if (Number.isFinite(ecart) && Math.abs(ecart) > 0.01) {
          dejaParts = ajouterPart(JSON.parse(JSON.stringify(dejaParts)), PART_CORRECTION, { net: ecart })
          correctionsManuelles.push({ code: r.bookingRef, lid: logId, ecart })
        }
      }
      // Tour 5 (point 4) : part « Payout ID » ↔ part datée identique : jamais
      // additionnées.
      if (dejaParts) {
        const dd = dedoublonnerPartsDatees(dejaParts, r.parts, 'net')
        partsFinales = fusionnerParts(dd.existantes, dd.nouvelles)
      } else {
        partsFinales = r.parts
      }
      eff = {
        ...r,
        net: sommeParts(partsFinales, 'net'),
        amount: sommeParts(partsFinales, 'amount'),
        commission: sommeParts(partsFinales, 'commission'),
        fee: sommeParts(partsFinales, 'fee'),
        tourismTax: sommeParts(partsFinales, 'tourismTax'),
      }
      netVal = Number(eff.net) || 0
      if (!match && netVal <= 0) {
        bumpReason('net_non_positif')
        ignored.push({ lines: lignesSource, bookingRef: r.bookingRef, guestName: r.guestName, checkIn: r.checkIn, reason: 'net_non_positif' })
        continue
      }
      // Tour 4 (point 1) : correction à la main (présente ou détectée) ET
      // remboursement dans ce relevé → on ne sait pas lequel l'emporte : refus,
      // « à trancher », montant inchangé.
      const rembourse = sommeNegatives(r.parts, 'net')
      const correctionPresente = !!(dejaParts && dejaParts[PART_CORRECTION] &&
        Math.abs(Number(dejaParts[PART_CORRECTION].net) || 0) > 0.01)
      if (match && rembourse < 0 && correctionPresente) {
        correctionsManuelles.pop()
        bumpReason('a_trancher_correction_remboursement')
        ignored.push({ lines: lignesSource, bookingRef: r.bookingRef, guestName: r.guestName, checkIn: r.checkIn, logId, reason: 'a_trancher_correction_remboursement' })
        continue
      }
      if (match && rembourse < 0) {
        remboursements.push({ code: r.bookingRef, lid: logId, rembourse, compte: _r2(match.montant) })
      }
      // Tour 4 (point 1) : JAMAIS négatif, ligne détaillée comprise.
      if (match && netVal < 0) {
        bumpReason('net_non_positif')
        ignored.push({ lines: lignesSource, bookingRef: r.bookingRef, guestName: r.guestName, checkIn: r.checkIn, reason: 'net_non_positif' })
        continue
      }
    }
    const origine = _origineStatsBooking(eff, netVal)
    const versements = partsFinales ? { parts_versements: partsFinales } : {}

    if (match) {
      const oldMontant = match.montant || 0
      // Une mensualité qui s'ajoute n'est pas un écrasement de montant.
      const overwritingManual = !partsValides(match.parts_versements) && !partsFinales &&
        oldMontant > 0 && Math.abs(oldMontant - netVal) > 0.01
      toUpdate.push({
        logId, month, moisEffectif,
        // Tour 5 (point 5) : refaite au clic sur Confirmer, factures relues.
        ...(cibleGarde ? { gardeDejaFacture: cibleGarde } : {}),
        existingResaId: match.id,
        bookingRef: r.bookingRef,
        old: { montant: oldMontant, menage: match.menage || 0 },
        new: {
          montant: netVal,
          menage: match.menage != null ? match.menage : menageForfait,
          checkIn: r.checkIn,
          checkOut: r.checkOut,
          nuits,
          voyageur: r.guestName,
          taxe: TAXE_BOOKING,
          bookingRef: r.bookingRef,
          ...origine,
          ...versements,
        },
        overwritingManual,
        rawLines: lignesSource,
      })
    } else {
      // Fix B-3 (2026-05-23) : skip si bookingRef dans tombstones (admin a
      // supprimé cette résa et ne souhaite pas la voir réinsérée).
      if (deletedBookingSet.has(r.bookingRef)) {
        bumpReason('tombstone_booking_ref')
        ignored.push({
          lines: lignesSource, bookingRef: r.bookingRef, guestName: r.guestName,
          checkIn: r.checkIn, reason: 'tombstone_booking_ref:' + r.bookingRef,
        })
        continue
      }
      // Plan B — clamp CREATE : pas de création sur un mois invoice ≤ avril
      // 2026 (canCreateMonth). N'affecte pas les UPDATE.
      if (!canCreateMonth(month)) {
        bumpReason('invoice_month_floor')
        ignored.push({
          lines: lignesSource, bookingRef: r.bookingRef, guestName: r.guestName,
          checkIn: r.checkIn, reason: 'invoice_month_floor:' + month,
        })
        continue
      }
      toCreate.push({
        logId, month, moisEffectif,
        ...(cibleGarde ? { gardeDejaFacture: cibleGarde } : {}),
        resa: {
          voyageur: r.guestName,
          plateforme: 'booking',
          checkIn: r.checkIn,
          date: r.checkIn,
          nuits,
          montant: netVal,
          menage: menageForfait,
          taxe: TAXE_BOOKING,
          notes: 'Import CSV Booking',
          bookingRef: r.bookingRef,
          ...origine,
          ...versements,
        },
        rawLines: lignesSource,
      })
    }
  }

  return {
    toUpdate, toCreate, ignored,
    // Références dont plusieurs lignes du relevé ont été additionnées (texte
    // affiché dans la fenêtre d'import).
    regroupements,
    // Point B : [{ code, lid, rembourse (<0), compte }] — texte à l'écran.
    remboursements,
    // Tour 3 point 2 : [{ code, lid, ecart, total }] — texte à l'écran.
    corrections: correctionsManuelles.map((c) => {
      const u = toUpdate.find((x) => x.logId === c.lid && (x.airbnbCode || x.bookingRef) === c.code)
      return { ...c, total: u ? u.new.montant : null, ...(c.ecartMenage != null && u ? { menage: u.new.menage } : {}) }
    }).filter((c) => c.total != null),
    summary: {
      totalRows: rows.length,
      toUpdateCount: toUpdate.length,
      toCreateCount: toCreate.length,
      ignoredCount: ignored.length,
      reasons,
    },
  }
}

function applyBookingImportPlan(data, plan, opts = {}) {
  const nd = JSON.parse(JSON.stringify(data || {}))
  // Chaque création / mise à jour porte l'origine CSV et la date d'import
  // (décision Dimitri 03/10 : savoir quel jour les relevés sont complets).
  const importeLe = opts.importeLe || new Date().toISOString()

  const ensureEntry = (month, logId) => {
    if (!nd[month]) nd[month] = {}
    if (!nd[month][logId]) nd[month][logId] = { reservations: [], extras: [] }
    if (!nd[month][logId].reservations) nd[month][logId].reservations = []
    if (!nd[month][logId].extras) nd[month][logId].extras = []
  }

  const genId = (bookingRef) =>
    'bdc_' + bookingRef + '_' + Math.random().toString(36).slice(2, 8)

  for (const u of plan.toUpdate) {
    ensureEntry(u.month, u.logId)
    const resas = nd[u.month][u.logId].reservations
    const ix = resas.findIndex(r => r.id === u.existingResaId)
    if (ix < 0) continue
    resas[ix] = {
      ...resas[ix],
      bookingRef: u.bookingRef,
      plateforme: 'booking',
      voyageur: u.new.voyageur || resas[ix].voyageur,
      checkIn: u.new.checkIn,
      date: u.new.checkIn,
      nuits: u.new.nuits || resas[ix].nuits,
      montant: u.new.montant,
      menage: u.new.menage != null ? u.new.menage : resas[ix].menage,
      taxe: u.new.taxe != null ? u.new.taxe : resas[ix].taxe,
      // Statistiques : absents du plan si le relevé ne les porte pas.
      ...(u.new.ca_brut_origine != null ? {
        ca_brut_origine: u.new.ca_brut_origine,
        ota_commission: u.new.ota_commission,
        montant_origine: u.new.montant_origine,
      } : {}),
      ...(u.new.parts_versements ? { parts_versements: u.new.parts_versements } : {}),
      origine_import: ORIGINE_CSV_BOOKING,
      importe_le: importeLe,
    }
  }

  for (const c of plan.toCreate) {
    ensureEntry(c.month, c.logId)
    nd[c.month][c.logId].reservations.push({
      ...c.resa,
      id: genId(c.resa.bookingRef),
      origine_import: ORIGINE_CSV_BOOKING,
      importe_le: importeLe,
    })
  }

  return nd
}

// ─────────────────────────────────────────────────────────────────────────────
// Maison Zen — rattacher chaque ligne d'un relevé Booking à SA chambre
// ─────────────────────────────────────────────────────────────────────────────
//
// proposerChambresBooking(data, rows, { groupe, chambreOuverte, nomsParId })
//   → Map(bookingRef → { lid, lu, raison })
// `lu: true` UNIQUEMENT si le relevé nomme l'annonce / la chambre (raison
// 'releve'). Sinon c'est une PROPOSITION (lu: false) que Dimitri valide à
// l'écran avant tout enregistrement :
//   'beds24'          la chambre que Beds24 (V2) donne pour ce séjour ;
//   'deja_importee'   la référence est déjà rangée dans une seule chambre ;
//   'calendrier'      une seule chambre a une arrivée Booking sans référence
//                     (ancien import iCal) ce jour-là ;
//   'chambre_ouverte' à défaut, la chambre depuis laquelle on importe.
// Fonction PURE, aucune écriture.
function _annonceVersChambre(annonce, groupe, nomsParId) {
  if (!annonce) return null
  const viaCarte = resolveListingId(annonce)
  if (viaCarte && groupe.includes(viaCarte)) return viaCarte
  const norm = normalizeListingName(annonce)
  const trouves = groupe.filter((lid) => {
    const nom = nomsParId && nomsParId[lid] ? normalizeListingName(nomsParId[lid]) : ''
    return nom && norm.includes(nom)
  })
  return trouves.length === 1 ? trouves[0] : null
}

function proposerChambresBooking(data, rows, opts = {}) {
  const groupe = Array.isArray(opts.groupe) ? opts.groupe : []
  const out = new Map()
  if (groupe.length === 0 || !Array.isArray(rows)) return out
  const mks = Object.keys(data || {}).filter((mk) => /^\d{4}-\d{2}$/.test(mk))
  const lignesDe = (lid) => {
    const all = []
    for (const mk of mks) {
      const resas = data[mk] && data[mk][lid] && data[mk][lid].reservations
      if (Array.isArray(resas)) all.push(...resas.filter(Boolean))
    }
    return all
  }
  const lignesParChambre = new Map(groupe.map((lid) => [lid, lignesDe(lid)]))
  // Relecture F : référence Booking → chambre du groupe d'après la V2.
  const beds24ParRef = new Map()
  const v2 = opts.resasByLogement
  if (v2 && typeof v2.get === 'function') {
    for (const lid of groupe) {
      const rowsV2 = v2.get(lid)
      for (const row of Array.isArray(rowsV2) ? rowsV2 : []) {
        const ref = row && row.raw_payload && row.raw_payload.apiReference
        if (!ref || String(row.status || '').toLowerCase() === 'cancelled') continue
        const l = groupe.includes(row.logement_id) ? row.logement_id : lid
        if (!beds24ParRef.has(String(ref))) beds24ParRef.set(String(ref), l)
      }
    }
  }
  for (const r of rows) {
    if (!r || r.type !== 'Reservation' || !r.bookingRef || out.has(r.bookingRef)) continue
    const lue = _annonceVersChambre(r.annonce, groupe, opts.nomsParId)
    if (lue) { out.set(r.bookingRef, { lid: lue, lu: true, raison: 'releve' }); continue }
    // Relecture F : la chambre que Beds24 donne pour ce séjour (V2 non
    // annulée) est proposée d'abord, motif « vue dans Beds24 ».
    const vueBeds24 = beds24ParRef.get(String(r.bookingRef))
    if (vueBeds24) { out.set(r.bookingRef, { lid: vueBeds24, lu: false, raison: 'beds24' }); continue }
    // Une référence supprimée d'une chambre (deleteResaV2) n'y est plus
    // « déjà importée » : on ne la repropose pas là.
    const deja = groupe.filter((lid) =>
      !_getDeletedBookingRefs(data, lid).has(r.bookingRef) &&
      lignesParChambre.get(lid).some((x) => x.bookingRef && x.bookingRef === r.bookingRef))
    if (deja.length === 1) { out.set(r.bookingRef, { lid: deja[0], lu: false, raison: 'deja_importee' }); continue }
    const memeJour = groupe.filter((lid) =>
      lignesParChambre.get(lid).some((x) =>
        !x.bookingRef && (x.checkIn || x.date) === r.checkIn &&
        String(x.plateforme || '').toLowerCase() === 'booking'))
    if (memeJour.length === 1) { out.set(r.bookingRef, { lid: memeJour[0], lu: false, raison: 'calendrier' }); continue }
    const defaut = groupe.includes(opts.chambreOuverte) ? opts.chambreOuverte : groupe[0]
    out.set(r.bookingRef, { lid: defaut, lu: false, raison: 'chambre_ouverte' })
  }
  return out
}

module.exports = {
  parseBookingDate,
  parseBookingCsv,
  buildBookingImportPlan,
  applyBookingImportPlan,
  proposerChambresBooking,
  DATE_PLANCHER_DEFAULT,
  // Helpers exposés (testés isolément + réutilisables par l'UI/détection).
  repairMojibake,
  normalizeHeader,
  normalizeTypeValue,
  MONTHS_FR,
}
